import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Verificar se há um usuário salvo no localStorage
    const savedUser = localStorage.getItem('auth_user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('auth_user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      console.log('Attempting login...');
      
      // Para fins de demo, usar login simplificado
      if (email === 'alexandremsgg@gmail.com' && password === '7070') {
        console.log('Login successful');
        const mockUser = {
          id: '1',
          name: 'Administrador',
          email: email,
          role: 'ADMIN',
          company_id: 'f8c3de3d-1fea-4d7c-a8b0-29f63c4c3454'
        };
        
        // Salvar usuário no localStorage
        localStorage.setItem('auth_user', JSON.stringify(mockUser));
        setUser(mockUser);

        // Redirecionar para a página original ou certificates
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get('returnTo');
        window.location.replace(returnTo || '/admin/certificates');
      } else {
        throw new Error('Email ou senha inválidos');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error('Email ou senha inválidos');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      console.log('Logging out...');
      
      // Limpar dados de autenticação
      localStorage.removeItem('auth_user');
      setUser(null);
      
      // Redirecionar para login
      window.location.replace('/login');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}